<template>
    <b-dropdown
        v-b-tooltip.hover.bottom="
            activeChat.lastConversation.assignedUserId && !assignee
                ? $t('entities.user.user-not-found')
                : $t('chat-assign-to-me')
        "
        :disabled="isAssignChatPending"
        class="active-chat-assign-dropdown overflow"
        right
        split
        split-class="d-block"
        variant="primary"
        @click="
            $emit('assign-chat', {
                id: activeChat.lastConversation.id,
                channelId: activeChat.channel.id,
                identityId: activeChat.lastConversation.identityId,
                tags: activeChat.tags,
                userId: getUserData().id,
                teamId:
                    activeChat.lastConversation.assignedTeamId &&
                    isUserPartOfTeam(activeChat.lastConversation.assignedTeamId)
                        ? activeChat.lastConversation.assignedTeamId
                        : getRandomUserTeamForChannel(activeChat.channel.id),
                status: activeChat.lastConversation.status,
            })
        "
        @shown="useFocus(searchQueryTemplateRef, { initialValue: true })"
    >
        <template #button-content>
            <template v-if="assignedUser">
                {{ assignedUser.name }}
                <small v-if="assignedTeam" class="user-team">
                    <font-awesome-icon :icon="['fas', 'users']" />
                    {{ assignedTeam.name }}
                </small>
            </template>
            <template v-else-if="assignedTeam">
                <font-awesome-icon :icon="['fas', 'users']" class="mr-50" />
                {{ assignedTeam.name }}
            </template>
            <template v-else>
                {{
                    activeChat.lastConversation.assignedUserId
                        ? $t("entities.user.user-not-found")
                        : $t("chat-assign-to-me")
                }}
            </template>
        </template>
        <b-dropdown-form>
            <b-form-input
                ref="searchQueryTemplateRef"
                v-model="dropdownSearchQuery"
                :placeholder="$t('Search')"
            ></b-form-input>
        </b-dropdown-form>
        <b-dropdown-item-button
            :active="!assignee"
            :disabled="!assignee"
            @click="
                $emit('assign-chat', {
                    id: activeChat.lastConversation.id,
                    channelId: activeChat.channel.id,
                    identityId: activeChat.lastConversation.identityId,
                    tags: activeChat.tags,
                    userId: null,
                    teamId: null,
                    status: activeChat.lastConversation.status,
                })
            "
        >
            {{ $t("entities.conversation.Unassigned") }}
        </b-dropdown-item-button>
        <b-dropdown-divider v-if="usersTeamsTree.usersTree.length > 0"></b-dropdown-divider>
        <b-dropdown-item-button
            v-for="user in usersTeamsTree.usersTree"
            :key="user.id"
            :active="user.id === activeChat.lastConversation.assignedUserId"
            :disabled="user.id === activeChat.lastConversation.assignedUserId"
            @click="
                $emit('assign-chat', {
                    id: activeChat.lastConversation.id,
                    channelId: activeChat.channel.id,
                    identityId: activeChat.lastConversation.identityId,
                    tags: activeChat.tags,
                    userId: user.id,
                    teamId: null,
                    status: activeChat.lastConversation.status,
                })
            "
        >
            <div :class="`badge-${resolveUserStatus(user.status)}`" class="badge user-status-badge"></div>
            {{ user.name }}
        </b-dropdown-item-button>

        <b-dropdown-divider v-if="usersTeamsTree.teamsTree.length > 0"></b-dropdown-divider>

        <b-dropdown-group v-for="team in usersTeamsTree.teamsTree" :key="`team-${team.id}`">
            <b-dropdown-item-button
                :active="!activeChat.lastConversation.assignedUserId && team.id === (assignee && assignee.id)"
                :disabled="!activeChat.lastConversation.assignedUserId && team.id === (assignee && assignee.id)"
                @click="
                    $emit('assign-chat', {
                        id: activeChat.lastConversation.id,
                        channelId: activeChat.channel.id,
                        identityId: activeChat.lastConversation.identityId,
                        tags: activeChat.tags,
                        teamId: team.id,
                        userId: null,
                        status: activeChat.lastConversation.status,
                    })
                "
            >
                <font-awesome-icon :color="team.color" class="mr-1" icon="users" />
                {{ team.name }}
            </b-dropdown-item-button>
            <b-dropdown-item-button
                v-for="teamUser in team.users"
                :key="`teamuser-${teamUser.id}`"
                :active="
                    (!activeChat.lastConversation.assignedTeamId ||
                        activeChat.lastConversation.assignedTeamId === team.id) &&
                    teamUser.id === activeChat.lastConversation.assignedUserId
                "
                :disabled="
                    (!activeChat.lastConversation.assignedTeamId ||
                        activeChat.lastConversation.assignedTeamId === team.id) &&
                    teamUser.id === activeChat.lastConversation.assignedUserId
                "
                class="ml-3"
                @click="
                    $emit('assign-chat', {
                        id: activeChat.lastConversation.id,
                        channelId: activeChat.channel.id,
                        identityId: activeChat.lastConversation.identityId,
                        tags: activeChat.tags,
                        userId: teamUser.id,
                        teamId: team.id,
                        status: activeChat.lastConversation.status,
                    })
                "
            >
                <div :class="`badge-${resolveUserStatus(teamUser.status)}`" class="badge user-status-badge" />
                {{ teamUser.name }}
            </b-dropdown-item-button>
        </b-dropdown-group>
    </b-dropdown>
</template>

<script>
import { computed, ref } from "@vue/composition-api";
import {
    BDropdown,
    BDropdownItemButton,
    BDropdownForm,
    BDropdownDivider,
    BDropdownGroup,
    BFormInput,
    VBTooltip,
} from "bootstrap-vue";
import { resolveUserStatus, resolveTeamsUsersTree } from "@/utils/users";
import { getUserData } from "@/auth/getUserData";
import { useFocus } from "@vueuse/core";

// Composables v2.1
import useUsers from "@/composables/useUsers";

export default {
    components: {
        BDropdown,
        BDropdownItemButton,
        BDropdownForm,
        BDropdownDivider,
        BDropdownGroup,
        BFormInput,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        teams: {
            type: Array,
            required: false,
        },
        activeChat: {
            type: Object,
            required: true,
        },
        isAssignChatPending: {
            type: Boolean,
            required: true,
        },
    },
    setup(props) {
        const { users, getRandomUserTeamForChannel, isUserPartOfTeam } = useUsers();

        const assignedUser = computed(() => {
            if (!props.activeChat.lastConversation?.assignedUserId) {
                return;
            }

            return users.value?.find((user) => user.id === props.activeChat.lastConversation.assignedUserId);
        });

        const assignedTeam = computed(() => {
            if (!props.activeChat.lastConversation.assignedTeamId) {
                return;
            }

            return props.teams.find((team) => team.id === props.activeChat.lastConversation.assignedTeamId);
        });

        const assignee = computed(() => assignedUser || assignedTeam);

        const searchQueryTemplateRef = ref(null);
        const dropdownSearchQuery = ref("");
        return {
            assignee,
            assignedUser,
            assignedTeam,
            resolveUserStatus,
            getUserData,
            usersTeamsTree: computed(() =>
                resolveTeamsUsersTree(props.teams, users.value, dropdownSearchQuery, props.activeChat.channel)
            ),
            dropdownSearchQuery,
            searchQueryTemplateRef,
            useFocus,
            getRandomUserTeamForChannel,
            isUserPartOfTeam,
        };
    },
};
</script>

<style lang="scss">
.dropdown-menu .dropdown-item {
    width: 100%;
}
.user-status-badge {
    height: 14px;
    width: 14px;
}
.user-status-badge:empty {
    display: inline-block;
}

.user-team {
    display: block;
    font-size: 10px;
    margin-top: 5px;
}
</style>
